import { differenceInSeconds } from 'date-fns';
import { toZonedTime } from 'date-fns-tz';
import { FC, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { Countdown, CountdownProps } from '@/components/Countdown';

import { ScCountdown, ScTimerLabel, ScTimerLabelProps } from './styled';

const SECONDS_IN_DAY = 86400;

interface TimerProps {
  endDate: number;
  onClick?: () => void;
}

const getTime = (time: number) => (time < 0 ? 0 : time);

export const Timer: FC<TimerProps> = ({ endDate, onClick }) => {
  const { t } = useTranslation();
  const timeLeft = useMemo(() => {
    const currentTimestamp = new Date();
    const zonedTime = toZonedTime(currentTimestamp, 'UTC');
    const time = differenceInSeconds(endDate, zonedTime);
    return getTime(time);
  }, [endDate]);

  return (
    <ScCountdown onClick={onClick}>
      <Countdown timeLeft={timeLeft} dual>
        {({ days, hours, minutes, seconds }) => (
          <>
            <div>
              <span className="bg" />
              <p>
                <span attr-label="dd">{days}</span> {t('timer.dd')}
              </p>
            </div>
            <div>
              <span className="bg" />
              <p>
                <span attr-label="hh">{hours}</span> {t('timer.hh')}
              </p>
            </div>
            <div>
              <span className="bg" />
              <p>
                <span attr-label="mm">{minutes}</span> {t('timer.mm')}
              </p>
            </div>
            <div>
              <span className="bg" />
              <p>
                <span attr-label="ss">{seconds}</span> {t('timer.ss')}
              </p>
            </div>
          </>
        )}
      </Countdown>
    </ScCountdown>
  );
};

export const TimerLabel: FC<
  Pick<CountdownProps, 'timeLeft'> & ScTimerLabelProps
> = ({ timeLeft, ...props }) => {
  const { t } = useTranslation();

  const isDaysShown = useMemo(() => timeLeft > SECONDS_IN_DAY, [timeLeft]);

  return (
    <ScTimerLabel {...props}>
      <Countdown timeLeft={getTime(timeLeft)} dual>
        {({ days, hours, minutes, seconds }) => (
          <>
            {isDaysShown && (
              <>
                <div>
                  {days}
                  {t('timer.dd')[0]}
                </div>
                <div>:</div>
              </>
            )}

            <div>
              {hours}
              {t('timer.hh')[0]}
            </div>
            <div>:</div>
            <div>
              {minutes}
              {t('timer.mm')[0]}
            </div>

            {!isDaysShown && (
              <>
                <div>:</div>
                <div>
                  {seconds}
                  {t('timer.ss')[0]}
                </div>
              </>
            )}
          </>
        )}
      </Countdown>
    </ScTimerLabel>
  );
};
